import './AccountLoyaltySubpageC.scss';

import classnames from 'classnames';

import { Accordion, AccordionItem } from '../../../Accordion';
import { AccountLoyaltySubpageCContentType } from '../../../contentTypes';
import { CtaFromContent } from '../../../CtaFromContent';
import { LoyaltyProgramForm } from '../../../form/LoyaltyProgramForm';
import { Div } from '../../../HTMLElement';
import { t } from '../../../i18n/i18n';
import { Icon } from '../../../Icon';
import { ImageFromContentV2 } from '../../../ImageFromContentV2';
import { useCustomer } from '../../../useCustomer';
import { formatHtml, uiClassName } from '../../../util';
import { AccountLoyaltySubpageProps } from '../AccountLoyaltySubpage';
import { AccountSubpage } from '../AccountSubpage';
import { Box } from '../accountSubpagesB/Box';

export const AccountLoyaltySubpageC = (props: AccountLoyaltySubpageProps) => {
  const customer = useCustomer({
    populate: {
      fidelityCards: true,
    },
  });

  const amount =
    customer?.fidelityCards && customer.fidelityCards.length >= 1 ? customer.fidelityCards[0].creditAmount : 0;

  return (
    <AccountSubpage<AccountLoyaltySubpageCContentType>>
      {(page) => {
        const lastItemPoints = page.content?.body.memberSection?.levels?.at(-1)?.fromPoints;
        const numberOfSteps = page.content?.body.memberSection?.levels?.length;

        let activeStep = 0;
        const activeLevelItem = page.content?.body.memberSection?.levels?.find((item, index) => {
          const valid =
            item.fromPoints !== undefined && item.toPoints && item.fromPoints <= amount && item.toPoints >= amount;
          if (valid) {
            activeStep = index + 1;
          }
          return valid;
        });

        const nextStepFromPoints = page.content?.body.memberSection?.levels?.at(activeStep)?.fromPoints;
        const remainedPointsForNextLevel =
          lastItemPoints && amount < lastItemPoints && nextStepFromPoints ? nextStepFromPoints - amount : 0;

        const memberContent = page.content?.body.memberSection;
        const noMemberContent = page.content?.body.noMemberSection;

        return (
          <div className="account-loyalty-page-c">
            {memberContent && customer && customer.loyaltyProgramConsent && (
              <>
                <Box>
                  <p
                    className={classnames(
                      uiClassName({
                        bs5: 'fw-bold mb-3',
                      }),
                      'page-title'
                    )}
                  >
                    {formatHtml(memberContent.title)} <strong>{activeLevelItem?.name}</strong>
                  </p>

                  {memberContent.topImage && (
                    <ImageFromContentV2
                      {...memberContent.topImage}
                      className={uiClassName({ bs5: 'mb-4 mb-md-5' })}
                      fluid
                    />
                  )}

                  {memberContent.description && (
                    <Div className="page-description" uiClassName={{ bs5: 'mb-4' }}>
                      {amount && (
                        <p className="remained-points">
                          {t('account.loyalty.pointsBalance', { balancePoints: amount })}
                        </p>
                      )}
                      <p className="remained-points">
                        {lastItemPoints &&
                          amount < lastItemPoints &&
                          t('account.loyalty.infoPointNextLevel', { points: remainedPointsForNextLevel })}
                      </p>
                      <p>{formatHtml(memberContent.description)}</p>
                    </Div>
                  )}

                  {activeLevelItem &&
                    memberContent.levels &&
                    memberContent.levels.length > 0 &&
                    lastItemPoints &&
                    numberOfSteps &&
                    activeStep > 0 && (
                      <Div className="new-point-bar">
                        {memberContent.iconBarName && (
                          <Icon
                            className="iconBar"
                            name={memberContent.iconBarName}
                            size={26}
                            style={{
                              color: memberContent.scoreLevelColor,
                              left: amount < lastItemPoints ? `${(100 / numberOfSteps) * activeStep}%` : '100%',
                              position: 'absolute',
                              top: '-30px',
                            }}
                          />
                        )}

                        <Div
                          className="bar"
                          style={{
                            backgroundColor: memberContent.scoreLevelColor,
                            width: amount < lastItemPoints ? `${(100 / numberOfSteps) * activeStep}%` : '100%',
                          }}
                        />
                        {memberContent.levels.map(
                          (item, index) =>
                            item.fromPoints !== undefined &&
                            item.toPoints && (
                              <Div
                                className={classnames('step', {
                                  'active-step': item.name === activeLevelItem.name || item.toPoints < amount,
                                })}
                                key={item.contentSectionId}
                                style={{
                                  left: index < numberOfSteps ? `${(100 / numberOfSteps) * (index + 1)}%` : '100%',
                                }}
                              >
                                <Div
                                  className={classnames('bullet-point')}
                                  style={{
                                    borderColor:
                                      item.name === activeLevelItem.name || item.toPoints < amount
                                        ? page.content?.body.memberSection?.scoreLevelColor
                                        : '#E0E0E0',
                                  }}
                                />
                                <Div className={classnames('step-name')}>{item.name}</Div>
                              </Div>
                            )
                        )}
                      </Div>
                    )}

                  <Div paddingY={{ md: 3, xs: 2 }} uiClassName={{ bs5: 'border-bottom' }}>
                    {memberContent.levels && (
                      <Div>
                        {memberContent.levels.map((item) => (
                          <Div
                            alignItems={{ xs: 'start' }}
                            className="rule-item"
                            display={'flex'}
                            flexDirection={{ xs: 'row' }}
                            key={item.contentSectionId}
                            paddingBottom={{ xs: 3 }}
                          >
                            {item.name && (
                              <>
                                {item.iconName && item.fromPoints !== undefined && item.toPoints && (
                                  <Icon
                                    className={classnames(uiClassName({ bs5: 'me-2' }), 'item-rule-icon')}
                                    name={item.iconName}
                                    size={20}
                                    style={{
                                      color: !activeLevelItem?.name
                                        ? page.content?.body.memberSection?.scoreLevelColor
                                        : item.name === activeLevelItem.name
                                        ? page.content?.body.memberSection?.scoreLevelColor
                                        : '#E0E0E0',
                                    }}
                                  />
                                )}
                                {item.name && (
                                  <Div
                                    className={'item-rule-name'}
                                    fontWeight={700}
                                    uiClassName={{ bs5: 'small me-2' }}
                                  >
                                    {formatHtml(item.name)}
                                  </Div>
                                )}
                                {item.description && (
                                  <Div className={'item-rule-description'} uiClassName={{ bs5: 'small' }}>
                                    {formatHtml(item.description)}
                                  </Div>
                                )}
                              </>
                            )}
                          </Div>
                        ))}
                      </Div>
                    )}
                  </Div>

                  {memberContent.legendSection && (
                    <Div
                      alignItems={'center'}
                      className="legend"
                      display={'flex'}
                      flexDirection={{ md: 'row', xs: 'column' }}
                      justifyContent={'spaceBetween'}
                      paddingY={{ lg: 4, xs: 3 }}
                      uiClassName={{ bs5: 'border-bottom mb-4 mb-md-5' }}
                    >
                      <Div alignItems={'center'} display={'flex'}>
                        {memberContent.legendSection.iconName && (
                          <Div
                            className={'legend-icon-wrapper'}
                            marginBottom={{ md: 0, xs: 3 }}
                            marginEnd={3}
                            style={{
                              background: memberContent.legendSection.backgroundIconColor,
                            }}
                          >
                            <Icon
                              className={'legend-icon'}
                              name={memberContent.legendSection.iconName}
                              size={40}
                              style={{
                                color: memberContent.legendSection.iconColor,
                              }}
                            />
                          </Div>
                        )}
                        {memberContent.legendSection.description && (
                          <p className={uiClassName({ bs5: 'mb-0' })}>
                            <b>{memberContent.legendSection.description}</b>
                          </p>
                        )}
                      </Div>
                      <Div>
                        <CtaFromContent {...memberContent.legendSection.couponLink} />
                      </Div>
                    </Div>
                  )}

                  {page.content?.body.faq && (
                    <Div className="faq" uiClassName={{ bs5: 'mb-md-5 mb-4' }}>
                      {page.content.body.faq.title && (
                        <p className="faq-title">{formatHtml(page.content.body.faq.title)}</p>
                      )}
                      {page.content.body.faq.faqList && page.content.body.faq.faqList.length > 0 && (
                        <Accordion id="accordion-loyalty">
                          {page.content.body.faq.faqList.map((accordionItem) => (
                            <AccordionItem
                              id={accordionItem.contentSectionId.toString()}
                              key={accordionItem.contentSectionId}
                              title={accordionItem.question ?? ''}
                            >
                              <Div className="accordion-content">{formatHtml(accordionItem.answer)}</Div>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      )}
                    </Div>
                  )}

                  {page.content?.body.ctaSection?.member && (
                    <Div alignItems={'center'} className="cta-section" display={'flex'} flexDirection={'column'}>
                      {page.content.body.ctaSection.member.firstCta && (
                        <Div>
                          <CtaFromContent {...page.content.body.ctaSection.member.firstCta} />
                        </Div>
                      )}
                      {page.content.body.ctaSection.member.secondCta && (
                        <Div uiClassName={{ bs5: 'mt-3' }}>
                          <CtaFromContent {...page.content.body.ctaSection.member.secondCta} />
                        </Div>
                      )}
                    </Div>
                  )}
                </Box>
              </>
            )}
            {noMemberContent && customer && !customer.loyaltyProgramConsent && (
              <>
                <Box>
                  {noMemberContent.title && (
                    <p
                      className={classnames(
                        uiClassName({
                          bs5: 'mb-4',
                        }),
                        'page-title'
                      )}
                    >
                      {formatHtml(noMemberContent.title)}
                    </p>
                  )}
                  {noMemberContent.description && <p>{formatHtml(noMemberContent.description)}</p>}

                  <LoyaltyProgramForm customer={customer} />

                  {page.content?.body.faq && (
                    <Div className="faq" uiClassName={{ bs5: 'mt-5 mb-md-5 mb-4' }}>
                      {page.content.body.faq.title && (
                        <p className="faq-title">{formatHtml(page.content.body.faq.title)}</p>
                      )}
                      {page.content.body.faq.faqList && page.content.body.faq.faqList.length > 0 && (
                        <Accordion id="accordion-loyalty">
                          {page.content.body.faq.faqList.map((accordionItem) => (
                            <AccordionItem
                              id={accordionItem.contentSectionId.toString()}
                              key={accordionItem.contentSectionId}
                              title={accordionItem.question ?? ''}
                            >
                              <Div className="accordion-content">{formatHtml(accordionItem.answer)}</Div>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      )}
                    </Div>
                  )}

                  {page.content?.body.ctaSection?.noMember && (
                    <Div alignItems={'center'} className="cta-section" display={'flex'} flexDirection={'column'}>
                      {page.content.body.ctaSection.noMember.firstCta && (
                        <Div>
                          <CtaFromContent {...page.content.body.ctaSection.noMember.firstCta} />
                        </Div>
                      )}

                      {page.content.body.ctaSection.noMember.secondCta && (
                        <Div uiClassName={{ bs5: 'mt-3' }}>
                          <CtaFromContent {...page.content.body.ctaSection.noMember.secondCta} />
                        </Div>
                      )}
                    </Div>
                  )}
                </Box>
              </>
            )}
          </div>
        );
      }}
    </AccountSubpage>
  );
};
